import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem('accessToken'));
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem('refreshToken'));
  const [userNickname, setUserNickname] = useState(() => localStorage.getItem('userNickname'));
  const [userId, setUserId] = useState(() => localStorage.getItem('userId'));
  const [userType, setUserType] = useState(() => localStorage.getItem('userType'));
  const navigate = useNavigate();

  // localStorage 동기화를 useCallback으로 최적화
  const updateLocalStorage = useCallback((key, value) => {
    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  }, []);

  useEffect(() => {
    updateLocalStorage('accessToken', accessToken);
  }, [accessToken, updateLocalStorage]);

  useEffect(() => {
    updateLocalStorage('refreshToken', refreshToken);
  }, [refreshToken, updateLocalStorage]);

  useEffect(() => {
    updateLocalStorage('userNickname', userNickname);
  }, [userNickname, updateLocalStorage]);

  useEffect(() => {
    updateLocalStorage('userId', userId);
  }, [userId, updateLocalStorage]);

  useEffect(() => {
    updateLocalStorage('userType', userType);
  }, [userType, updateLocalStorage]);

  const login = useCallback(async (userId, userPw) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId, user_pw: userPw }),
        credentials: 'include', // 쿠키를 포함하기 위해 추가
      });
      const data = await response.json();
      if (data.status === 'success') {
        setAccessToken(data.data.access_token);
        setRefreshToken(data.data.refresh_token);
        setUserNickname(data.data.user_nickname);
        setUserId(data.data.user_id);
        setUserType(data.data.user_type);
        return true;
      }
      return false;
    } catch (error) {
      console.error('로그인 오류:', error);
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    setUserNickname(null);
    setUserId(null);
    setUserType(null);
    navigate('/signin');
  }, [navigate]);

  const refreshAccessToken = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/tokens/access/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      const data = await response.json();
      if (data.status === 'success') {
        setAccessToken(data.data.access_token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('토큰 갱신 오류:', error);
      return false;
    }
  }, [refreshToken]);

  const getAccessToken = useCallback(async () => {
    // 토큰이 없는 경우 처리
    if (!accessToken && refreshToken) {
      const success = await refreshAccessToken();
      if (!success) {
        logout();
        return null;
      }
    } else if (!accessToken && !refreshToken) {
      return null;
    }

    // JWT 토큰 만료 시간 확인 함수
    const isTokenExpired = (token) => {
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(window.atob(base64));
        return payload.exp * 1000 < Date.now();
      } catch (e) {
        return true;
      }
    };

    // 토큰이 만료된 경우에만 검증 API 호출
    if (accessToken && isTokenExpired(accessToken)) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/tokens/access/validate`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });

        if (response.status === 401) {
          logout();
          return null;
        }
      } catch (error) {
        console.error('토큰 검증 오류:', error);
        logout();
        return null;
      }
    }

    return accessToken ? `Bearer ${accessToken}` : null;
  }, [accessToken, refreshToken, refreshAccessToken, logout]);

  const handleApiError = useCallback((error) => {
    // 401 에러와 메시지가 일치하는 경우 처리
    if (error.response?.status === 401 || 
        error.response?.data?.message === 'Invalid or expired token' ||
        error.message === 'Invalid or expired token') {
      setAccessToken(null);
      setRefreshToken(null);
      setUserNickname(null);
      setUserId(null);
      setUserType(null);
      navigate('/signin');
      return true;
    }
    return false;
  }, [navigate]);

  // Context value를 메모화하여 불필요한 리렌더링 방지
  const contextValue = useMemo(() => ({
    login, 
    logout, 
    getAccessToken, 
    userNickname, 
    userId, 
    userType, 
    handleApiError 
  }), [login, logout, getAccessToken, userNickname, userId, userType, handleApiError]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
